import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import { MAX_PINNED_WIDGETS, asPinnableTwin, drawnFigures, pinnableFigures } from "@libs/rqa-sim-widgets";
import $api from "@/zapi-frontend/shared/api/axios";
import { LEDGER_REFETCH_MS, useModelLedger } from "../ModelLedgerIsland/useModelLedger";
import {
  baseSelection,
  dimensionKeys,
  dragKnob,
  isKnobInert,
  knobRange,
  knobStart,
  pickOption,
  resetTwin,
  selectionOf,
  setMembership,
  solveTwin,
  spellTwinParts,
  spellTwinValue,
  twinFigures,
  twinGraph,
  varName,
  varUnit
} from "@/zapi-frontend/shared/lib/rqaSim/twin";
import type { TRqaSimTwin, TRqaSimWidget } from "@/zapi-frontend/shared/types/rqaSim";
import type { TTouched, TTwinSelection, TTwinValues } from "@/zapi-frontend/shared/lib/rqaSim/twin";

export const RQA_SIM_QUERY_KEY = "project-rqa-sim";

type TRqaSimPayload = { twin: TRqaSimTwin; widgets: TRqaSimWidget[] } | null;

const rqaSimQuery = (projectId: string) => ({
  queryKey: [RQA_SIM_QUERY_KEY, projectId],
  queryFn: async (): Promise<TRqaSimPayload> => {
    const response = await $api.get<{ data: { twin: TRqaSimTwin; widgets?: TRqaSimWidget[] } | null }>(
      `/projects/${projectId}/rqa-sim`
    );
    const data = response.data.data;
    return data ? { twin: data.twin, widgets: data.widgets ?? [] } : null;
  },
  enabled: !!projectId,
  staleTime: 60000,
  refetchInterval: LEDGER_REFETCH_MS
});

/** The twin alone. The sidebar needs it to decide whether the row exists at all — a project whose
 *  run produced no usable model has no page to offer. */
export function useRqaSimTwin(projectId: string) {
  return useQuery({ ...rqaSimQuery(projectId), select: (data: TRqaSimPayload) => data?.twin ?? null });
}

export function useRqaSimWidgets(projectId: string) {
  return useQuery({ ...rqaSimQuery(projectId), select: (data: TRqaSimPayload) => data?.widgets ?? [] });
}

const UNDO_MS = 5000;

export function useRqaSimWidgetActions(projectId: string, onFailure: (message: string) => void) {
  const queryClient = useQueryClient();
  const [hidden, setHidden] = useState<number[]>([]);
  const hiddenRef = useRef<number[]>([]);
  const hide = (next: number[]) => {
    hiddenRef.current = next;
    setHidden(next);
  };
  const unhide = (widgetId: number) => hide(hiddenRef.current.filter((id) => id !== widgetId));
  const key = [RQA_SIM_QUERY_KEY, projectId];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });
  const patch = (update: (widgets: TRqaSimWidget[]) => TRqaSimWidget[]) =>
    queryClient.setQueryData<TRqaSimPayload>(key, (data) => (data ? { ...data, widgets: update(data.widgets) } : data));

  const fail = (error: unknown) =>
    onFailure(
      isAxiosError(error) ? (error.response?.data as { message?: string })?.message ?? error.message : String(error)
    );

  const add = useMutation({
    mutationFn: async (widget: { key: string }) => {
      const response = await $api.post<{ data: TRqaSimWidget }>(`/projects/${projectId}/rqa-sim/widgets`, widget);
      return response.data.data;
    },
    onSuccess: (widget) => {
      patch((widgets) => [...widgets, widget]);
      invalidate();
    },
    onError: fail
  });

  const describe = useMutation({
    mutationFn: async ({ prompt }: { prompt: string }) => {
      const response = await $api.post<{ data: TRqaSimWidget }>(`/projects/${projectId}/rqa-sim/widgets/describe`, {
        prompt
      });
      return response.data.data;
    },
    onSuccess: (widget) => {
      patch((widgets) => [...widgets, widget]);
      invalidate();
    },
    onError: fail
  });

  const remove = useMutation({
    mutationFn: async (widgetId: number) => {
      try {
        await $api.delete(`/projects/${projectId}/rqa-sim/widgets/${widgetId}`);
      } catch (error) {
        if (!isAxiosError(error) || error.response?.status !== 404) throw error;
      }
      return widgetId;
    },
    onMutate: async (widgetId: number) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TRqaSimPayload>(key);
      patch((widgets) => widgets.filter((widget) => widget.id !== widgetId));
      return { previous };
    },
    onError: (error, _widgetId, context) => {
      if (context?.previous) queryClient.setQueryData<TRqaSimPayload>(key, context.previous);
      fail(error);
    },
    onSettled: (_data, _error, widgetId) => {
      unhide(widgetId);
      invalidate();
    }
  });

  const unpin = (widgetId: number, labels: { removed: string; undo: string }) => {
    hide([...hiddenRef.current, widgetId]);
    const commit = () => {
      if (hiddenRef.current.includes(widgetId)) remove.mutate(widgetId);
    };
    toast(labels.removed, {
      duration: UNDO_MS,
      action: { label: labels.undo, onClick: () => unhide(widgetId) },
      onAutoClose: commit,
      onDismiss: commit
    });
  };

  return { add, describe, unpin, hidden };
}

export function useRqaSim(projectId: string) {
  const ledgerQuery = useModelLedger(projectId);
  const twinQuery = useRqaSimTwin(projectId);
  const widgetsQuery = useRqaSimWidgets(projectId);

  const twin = twinQuery.data ?? null;
  const figures = useMemo(() => ledgerQuery.data?.figures ?? [], [ledgerQuery.data]);
  const graph = useMemo(() => twinGraph(figures), [figures]);

  const [touched, setTouched] = useState<TTouched>(null);
  const opening = useMemo(() => (twin ? baseSelection(twin) : null), [twin]);
  const selection: TTwinSelection = useMemo(
    () => (twin ? selectionOf(twin, touched) : { options: {}, inView: [], knobs: {} }),
    [twin, touched]
  );

  const solution = useMemo(() => (twin ? solveTwin(twin, graph, selection) : null), [twin, graph, selection]);
  const figuresAtSettings = useMemo(
    () => (solution && twin ? twinFigures(figures, solution.values, twin.vars) : figures),
    [figures, solution, twin]
  );

  const rangeOf = useCallback(
    (knob: { key: string; min?: number; max?: number; step?: number }) => knobRange(knob, graph),
    [graph]
  );

  const unitOf = useCallback(
    (key: string) => graph.byKey.get(key)?.unit ?? (twin ? varUnit(twin.vars[key]) : null),
    [graph, twin]
  );
  const dpOf = useCallback((key: string) => graph.byKey.get(key)?.dp ?? null, [graph]);
  const labelOf = useCallback(
    (key: string) => graph.byKey.get(key)?.name ?? (twin ? varName(twin.vars[key]) : null) ?? key,
    [graph, twin]
  );

  const knobValue = useCallback(
    (key: string) => {
      const knob = twin?.knobs.find((candidate) => candidate.key === key);
      if (!knob) return 0;
      return selection.knobs[key] ?? knobStart(key, knob.base, graph);
    },
    [twin, selection, graph]
  );

  const knobBase = useCallback(
    (key: string) => {
      const knob = twin?.knobs.find((candidate) => candidate.key === key);
      return knob ? knobStart(key, knob.base, graph) : 0;
    },
    [twin, graph]
  );

  const setByReader = useCallback(
    (key: string) => selection.knobs[key] !== undefined && selection.knobs[key] !== knobBase(key),
    [selection, knobBase]
  );
  const values = solution?.values;
  const twinValues: TTwinValues = useMemo(
    () => ({ valueOf: (key: string) => values?.get(key), unitOf, dpOf, setByReader }),
    [values, unitOf, dpOf, setByReader]
  );
  const spell = useCallback(
    (key: string, value: number | undefined) =>
      value === undefined ? undefined : spellTwinValue(twinValues, key, value),
    [twinValues]
  );
  const spellParts = useCallback(
    (key: string, value: number | undefined) =>
      value === undefined ? undefined : spellTwinParts(twinValues, key, value),
    [twinValues]
  );
  const isLedgerKey = useCallback((key: string) => graph.byKey.has(key), [graph]);

  const setKnob = useCallback(
    (key: string, value: number) => setTouched((current) => dragKnob(current, key, value)),
    []
  );
  const pickOptionAt = useCallback(
    (dimensionId: string, optionId: string) =>
      setTouched((current) => (twin ? pickOption(twin, current, dimensionId, optionId) : current)),
    [twin]
  );
  const setInView = useCallback((inView: string[]) => setTouched((current) => setMembership(current, inView)), []);
  const reset = useCallback(() => setTouched(resetTwin()), []);

  // Reset offers to restore the numbers, so it appears when a number differs from the memo and
  // not otherwise: a slider dragged back to where it started, or a chip whose vector equals the
  // base, has moved nothing. Membership counts, because every figure downstream of a segment set
  // aside is reading something the run did not publish.
  const isTouched = useMemo(() => {
    if (!twin || !opening || !solution) return false;
    const controlled = new Set<string>(twin.knobs.map((knob) => knob.key));
    for (const dimension of twin.dimensions) for (const key of dimensionKeys(dimension)) controlled.add(key);
    for (const key of controlled) {
      if (solution.values.get(key) !== solution.baseValues.get(key)) return true;
    }
    return [...selection.inView].sort().join() !== [...opening.inView].sort().join();
  }, [twin, opening, solution, selection.inView]);

  const pinnableTwin = useMemo(() => asPinnableTwin(twin), [twin]);
  const drawn = useMemo(() => drawnFigures(pinnableTwin), [pinnableTwin]);
  const widgets = useMemo(
    () =>
      (widgetsQuery.data ?? []).filter((widget) =>
        widget.kind === "block"
          ? widget.block !== null
          : widget.key !== null && !drawn.has(widget.key) && (graph.byKey.has(widget.key) || !!twin?.vars[widget.key])
      ),
    [widgetsQuery.data, drawn, graph, twin]
  );
  const pinnable = useMemo(
    () => [
      ...pinnableFigures(
        figures,
        pinnableTwin,
        widgets.flatMap((widget) => (widget.key ? [widget.key] : []))
      ).values()
    ],
    [figures, pinnableTwin, widgets]
  );
  const pinsLeft = MAX_PINNED_WIDGETS - widgets.length;

  return {
    isLoading: ledgerQuery.isLoading || twinQuery.isLoading,
    twin,
    widgets,
    pinnable,
    pinsLeft,
    hasLedger: figures.length > 0,
    selection,
    values: solution?.values ?? new Map<string, number>(),
    baseValues: solution?.baseValues ?? new Map<string, number>(),
    wholeValues: solution?.wholeValues ?? new Map<string, number>(),
    figuresAtSettings,
    twinValues,
    unresolved: solution?.unresolved ?? [],
    noReturn: solution?.noReturn ?? [],
    spell,
    spellParts,
    isLedgerKey,
    rangeOf,
    labelOf,
    knobValue,
    knobBase,
    setKnob,
    pickOption: pickOptionAt,
    setInView,
    reset,
    isTouched,
    isInert: (segments: string[] | undefined) => isKnobInert(segments, selection.inView)
  };
}

export type TRqaSim = ReturnType<typeof useRqaSim>;
